import React, { useState, useEffect, useContext,  useMemo } from "react";
import TableStyle from "components/Content/Table/TableStyle.module.css";
import TableAllData from "components/Content/CustomTable/TableAllData";
import { Box, Button, IconButton,Tooltip } from "@mui/material";
import ButtonStyle from "components/Button/ButtonStyle.module.css";
import AximTooltip from "components/AximTooltip";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog";

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import { Utils } from "common/Utils";

import EmailTemplateDialog from "components/Content/Modal/EmailTemplateDialog";

export default function Communication({ isVisible, isAdmin, isReadonly }) {
    const { companyId, error } = useContext(PrivateRoutesContext);
    const [isLoading, setIsLoading] = useState(false);
    const [templates, setTemplates] = useState([]);

    const [columnsArray, setColumnsArray] = useState([]);
    const [isEmailTemplateDialogOpen, setIsEmailTemplateDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmCloseAction, setConfirmCloseAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmButtonsDisabled, setConfirmButtonsDiabled] = useState(false);

    useEffect(() => {
        if (companyId && isVisible) {
            getEmailTemplates(companyId);
        }
      }, [companyId, isVisible]);

    useEffect(() => {
        var actionColumn = 
        {
            id: "emailTemplateId",
            accessor: (cell) => <>
                <AximTooltip arrow title="Edit Template">
                    <IconButton color="primary" onClick={() => editTemplate(cell)}><EditOutlinedIcon /></IconButton>
                </AximTooltip>
                <AximTooltip arrow title="Remove Template">
                    <IconButton color="primary" onClick={() => promptRemoveTemplate(cell)}><DeleteOutlineOutlinedIcon /></IconButton>
                </AximTooltip>
                {cell.isTestEmailRequired ? 
                    <AximTooltip
                        arrow
                        title={"Test Email is required before Employee Emails can be sent"}
                    >
                        <ErrorIcon color="Red" />
                    </AximTooltip> : 
                    null}
            </>,
            disableFilters: true,
        };

        if (isReadonly && !isAdmin) {
            setColumnsArray(columns);
        }
        else {
            setColumnsArray([actionColumn, ...columns]);
        }

    }, [isReadonly]);

    const getEmailTemplates = (companyId) => {
        setIsLoading(true);

        apiGet(apiUrls.getEmailTemplates(companyId)).then(
            (response) => {
                setTemplates(response.data);
                setIsLoading(false);
            }
        ).catch(
          (response) => {
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred loading templates.");
            error.setDoShowError(true);
            setIsLoading(false);
          }
        );
    }

    const newTemplate = () => {
        setSelectedTemplate(null);
        setIsEmailTemplateDialogOpen(true);
    }

    const editTemplate = (cell) => {
        setSelectedTemplate(cell);
        setIsEmailTemplateDialogOpen(true);
    }

    const promptRemoveTemplate = (cell) => {
        setConfirmCloseAction(() => (result) => removeTemplate(result, cell));
        setConfirmTitle('Remove Template');
        setConfirmText(`Are you sure you would like to remove the ${cell.emailTemplateDescription} template?`);
        setDoShowConfirm(true);
    }

    const removeTemplate = (isConfirmed, cell) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);

            apiPost(apiUrls.removeEmailTemplate(companyId, cell.emailTemplateId)).then(
                (response) => {
                    setTemplates(response.data);
                }
            ).catch(
              (response) => {
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading templates.");
                error.setDoShowError(true);
                setIsLoading(false);
              }
            )
            .finally(() => {
                setConfirmButtonsDiabled(false);
                setDoShowConfirm(false);
            });
          }
          else {
            setDoShowConfirm(false);
          }
    }

    const closeTemplateDialog = () => {
        setIsEmailTemplateDialogOpen(false);
        getEmailTemplates(companyId);
    }

    const handleConfirmClose = (result) => {
        confirmCloseAction(result);
    }

    const columns = [
        {
            Header: "Template",
            accessor: (cell) => <>
                {cell.emailTemplateDescription}
            </>,
        },
        {
            Header: "Update Date",
            accessor: (cell) => Utils.formatDate(cell.updateDate),
            Cell: ({row}) => (
                <Box>
                    {Utils.formatDate(row.original.updateDate)}
                </Box>
            ),
            sortType: "dateSort",
        },
        {
            Header: "Update By",
            accessor: (cell) => `${cell.updateUserFirstName} ${cell.updateUserLastName}`,
        },
      ];

    return (
        <Box>
            <Box className={TableStyle.tableWrapper}>
                <TableAllData
                    columns={columnsArray}
                    data={templates}
                    isLoading={isLoading}
                    headerContents={isReadonly && !isAdmin ? null :
                        <AximTooltip title="Add Template" disableFocusListener>
                            <IconButton
                                color="primary"
                                aria-label="Add Template"
                                component="span"
                                onClick={newTemplate}
                            >
                                <AddCircleOutlineOutlinedIcon />
                            </IconButton>
                        </AximTooltip>
                    }
              />
            </Box>

            <EmailTemplateDialog isOpen={isEmailTemplateDialogOpen} handleClose={closeTemplateDialog} templateToEdit={selectedTemplate} />

            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText}
                buttonsDisabled={confirmButtonsDisabled} />
        </Box>
    )
}